"use client";
import { useEffect, useRef } from "react";
import { useSetAtom, useStore } from "jotai";
import { marketingContextAtom } from "./marketingContextAtom";

/**
 * Scroll-depth + time-on-page for the marketing context.
 *
 * PERF: `marketingContextAtom` is persisted (localStorage), so every write is a
 * synchronous `JSON.stringify` + `setItem` of a ~25-field object *and* a re-render of
 * every subscriber. The previous version wrote on each new maximum depth — i.e. on
 * nearly every animation frame of a downward scroll, which was a measured cause of
 * scroll jank. Now the depth lives in a ref during scroll and is published only:
 *   • when a milestone bucket is crossed, at idle, and
 *   • once when the page is hidden.
 */
const BUCKETS = [25, 50, 75, 90, 100];

/** Largest milestone at or below `pct` (0 when below the first bucket). */
const bucketFor = (pct: number) =>
  BUCKETS.reduce((acc, b) => (pct >= b ? b : acc), 0);

export default function useEngagementMetrics() {
  // Write-only: `useAtom` would subscribe this hook's host component to every
  // marketing-context change and re-render it on each update.
  const setCtx = useSetAtom(marketingContextAtom);
  const store = useStore();
  const maxPctRef = useRef(0);
  const lastBucketRef = useRef(0);
  // Stamped inside the effect: `Date.now()` is impure, so it must not run during render.
  const startRef = useRef(0);

  useEffect(() => {
    startRef.current = Date.now();
    // Read the persisted max once, without subscribing, so we never publish a value
    // lower than what this session already recorded.
    const initial =
      store.get(marketingContextAtom)?.engagement?.max_scroll_pct ?? 0;
    maxPctRef.current = Math.max(maxPctRef.current, initial);
    lastBucketRef.current = bucketFor(maxPctRef.current);

    let ticking = false;
    // Cache the document height; recompute only when the layout can actually change,
    // instead of forcing a layout read on every scroll frame.
    let docHeight = document.documentElement.scrollHeight;
    const ro = new ResizeObserver(() => {
      docHeight = document.documentElement.scrollHeight;
    });
    ro.observe(document.documentElement);

    const publish = (extra?: { time_on_page_ms: number }) => {
      setCtx((prev) => ({
        ...prev,
        engagement: {
          ...(prev.engagement ?? {}),
          max_scroll_pct: maxPctRef.current,
          ...(extra ?? {}),
        },
      }));
    };

    const flush = () => {
      const bucket = bucketFor(maxPctRef.current);
      if (bucket <= lastBucketRef.current) return;
      lastBucketRef.current = bucket;
      publish();
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (!docHeight) return;
        const pct = Math.round(
          ((window.scrollY + window.innerHeight) / docHeight) * 100,
        );
        // Ref only — no state update, no storage write on the scroll path.
        if (pct > maxPctRef.current) maxPctRef.current = Math.min(pct, 100);
      });
    };

    const idleFlush = () => {
      const ric = (
        window as Window & {
          requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void;
        }
      ).requestIdleCallback;
      if (ric) ric(flush, { timeout: 2000 });
      else setTimeout(flush, 500);
    };

    // `visibilitychange` → hidden instead of `beforeunload`: it is bf-cache safe
    // (beforeunload disables the back/forward cache) and fires reliably on mobile,
    // where tab-switching / app-backgrounding is how a session normally ends.
    const onVisibility = () => {
      if (document.visibilityState !== "hidden") return;
      publish({ time_on_page_ms: Date.now() - startRef.current });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    const interval = setInterval(idleFlush, 5000);

    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
      ro.disconnect();
    };
  }, [setCtx, store]);
}
